import { lastTouch, wakeAt, type Card } from '@hmawiw/shared';
import { NavLink } from 'react-router';
import { useNow } from '../../hooks/useMedia';
import { useT } from '../../i18n';
import { useHour12 } from '../../lib/clock';
import { dueDays, formatShortDate, formatTime, hasDueTime, isLate } from '../../lib/dates';
import { formatRelative } from '../../lib/device';
import { reviewOf } from '../../lib/review';
import { deletedCards, doneCards, restingCards, useCards, useBoardCards } from '../../store/cards';
import { useSession } from '../../store/session';
import { useUi } from '../../store/ui';
import { CardSheet } from '../CardSheet/CardSheet';
import { StatsTab } from '../Stats/Stats';
import controls from '../controls.module.css';
import { ChevronLeft, EnergyGlyph } from '../icons';
import styles from './Lists.module.css';

export type ListTab = 'done' | 'resting' | 'deleted' | 'review' | 'stats';

const TABS: { key: ListTab; path: string }[] = [
  { key: 'done', path: '/done' },
  { key: 'resting', path: '/resting' },
  { key: 'deleted', path: '/deleted' },
  { key: 'review', path: '/review' },
  { key: 'stats', path: '/stats' },
];

/** Done, Resting, Recently deleted and the weekly review: one page, one tab each, one route each so links work. */
export function Lists({ tab }: { tab: ListTab }) {
  const t = useT();
  return (
    <div className={styles.page}>
      <header className={styles.head}>
        <NavLink to="/" className={controls.iconbtn} aria-label={t.t('nav.back')}>
          <ChevronLeft size={24} />
        </NavLink>
        <h1 className={`display ${styles.title}`}>{t.t(`lists.${tab}`)}</h1>
      </header>
      <nav className={styles.tabs} aria-label={t.t('lists.tabs')}>
        {TABS.map((x) => (
          <NavLink key={x.key} to={x.path} className={styles.tab} aria-current={x.key === tab ? 'page' : undefined}>
            {t.t(`lists.${x.key}`)}
          </NavLink>
        ))}
      </nav>
      <main className={styles.list}>
        {tab === 'done' ? <DoneTab /> : tab === 'resting' ? <RestingTab /> : tab === 'deleted' ? <DeletedTab /> : tab === 'stats' ? <StatsTab /> : <ReviewTab />}
      </main>
      <CardSheet />
    </div>
  );
}

function DoneTab() {
  const t = useT();
  const cards = useBoardCards();
  const restore = useCards((s) => s.restore);
  const list = doneCards(cards);
  return (
    <>
      {list.length === 0 ? <p className={styles.empty}>{t.t('done.empty')}</p> : null}
      {list.map((card) => (
        <div key={card.id} className={styles.item}>
          <span className={styles.itemTitle}>{card.title}</span>
          <button type="button" className={`${controls.btn} ${controls.quiet} ${styles.restore}`} onClick={() => void restore(card.id)}>
            {t.t('done.restore')}
          </button>
        </div>
      ))}
    </>
  );
}

function RestingTab() {
  const t = useT();
  const cards = useBoardCards();
  const wake = useCards((s) => s.wake);
  const openSheet = useUi((s) => s.openSheet);
  const now = useNow();
  const list = restingCards(cards, now);
  return (
    <>
      <p className={styles.hint}>{t.t('resting.hint')}</p>
      {list.length === 0 ? <p className={styles.empty}>{t.t('resting.empty')}</p> : null}
      {list.map((card) => (
        <div key={card.id} className={styles.item}>
          <button type="button" className={`${styles.itemTitle} ${styles.itemOpen}`} onClick={() => openSheet(card.id)}>
            {card.title}
            <span className={styles.when}>{t.t('resting.returns', { date: formatShortDate(wakeAt(card) ?? now, t.locale) })}</span>
          </button>
          <button type="button" className={`${controls.btn} ${controls.quiet} ${styles.restore}`} onClick={() => void wake(card.id)}>
            {t.t('repeat.wake')}
          </button>
        </div>
      ))}
    </>
  );
}

function DeletedTab() {
  const t = useT();
  const cards = useBoardCards();
  const undelete = useCards((s) => s.undelete);
  const tombstoneDays = useSession((s) => s.meta?.tombstoneDays ?? 90);
  const now = useNow();
  const deleted = deletedCards(cards);
  return (
    <>
      <p className={styles.hint}>{t.t('deleted.hint', { n: tombstoneDays })}</p>
      {deleted.length === 0 ? <p className={styles.empty}>{t.t('deleted.empty')}</p> : null}
      {deleted.map((card) => (
        <div key={card.id} className={styles.item}>
          <span className={styles.itemTitle}>
            {card.title}
            <span className={styles.when}>{formatRelative(card.deletedAt ?? now, now, t.locale)}</span>
          </span>
          <button type="button" className={`${controls.btn} ${controls.quiet} ${styles.restore}`} onClick={() => void undelete(card.id)}>
            {t.t('done.restore')}
          </button>
        </div>
      ))}
    </>
  );
}

function ReviewTab() {
  const t = useT();
  const cards = useBoardCards();
  const now = useNow();
  const review = reviewOf(cards, now);
  const empty = review.overdue.length + review.stuck.length + review.quiet.length + review.stale.length === 0;
  return (
    <>
      <p className={styles.hint}>{t.t('review.hint')}</p>
      {empty ? <p className={styles.empty}>{t.t('review.empty')}</p> : null}
      <ReviewSection title={t.t('review.overdue')} cards={review.overdue} now={now} />
      <ReviewSection title={t.t('review.stuck')} cards={review.stuck} now={now} />
      <ReviewSection title={t.t('review.quiet')} cards={review.quiet} now={now} />
      <ReviewSection title={t.t('review.stale')} cards={review.stale} now={now} />
    </>
  );
}

function ReviewSection({ title, cards, now }: { title: string; cards: Card[]; now: number }) {
  const t = useT();
  const hour12 = useHour12();
  const openSheet = useUi((s) => s.openSheet);
  if (cards.length === 0) return null;
  const dueText = (card: Card): string | null => {
    if (card.dueAt === null) return null;
    const days = dueDays(card.dueAt, now);
    if (isLate(card.dueAt, now)) return days < 0 ? t.tn('due.late', -days) : t.t('due.lateToday');
    const base = days === 0 ? t.t('due.today') : days === 1 ? t.t('due.tomorrow') : days < 7 ? t.t('due.inDays', { n: days }) : t.t('due.on', { date: formatShortDate(card.dueAt, t.locale) });
    return hasDueTime(card.dueAt) ? `${base} ${formatTime(card.dueAt, t.locale, hour12)}` : base;
  };
  return (
    <section className={styles.section}>
      <h2 className={styles.sectionTitle}>
        {title} <span className={styles.count}>{cards.length}</span>
      </h2>
      {cards.map((card) => {
        const due = dueText(card);
        return (
          <button key={card.id} type="button" className={styles.reviewRow} onClick={() => openSheet(card.id)}>
            <span className={styles.reviewMain}>
              <span className={styles.itemTitle}>{card.title}</span>
              <span className={styles.reviewMeta}>
                <span className={styles.column} style={{ color: `var(--m${card.motivation})` }}>
                  {t.column(card.motivation)}
                </span>
                <span>{t.band(card.band)}</span>
                {due ? <span className={card.dueAt !== null && isLate(card.dueAt, now) ? styles.late : styles.due}>{due}</span> : null}
                {card.repeat?.kind === 'ongoing' ? (
                  <span className={styles.late}>{t.t('card.last', { when: formatRelative(lastTouch(card), now, t.locale) })}</span>
                ) : (
                  <span>{t.t(card.updatedAt === card.createdAt ? 'review.added' : 'review.touched', { when: formatRelative(card.updatedAt, now, t.locale) })}</span>
                )}
              </span>
            </span>
            <span className={styles.glyph}>
              <EnergyGlyph level={card.energy} size={16} />
            </span>
          </button>
        );
      })}
    </section>
  );
}
